import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs/operators';

import { FormComponent, FormComponentProps } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Integer from '../../../../components/form/elements/integer';
import Readonly from '../../../../components/form/elements/readonly';
import Select from '../../../../components/form/elements/select';
import Text from '../../../../components/form/elements/text';
import Watch, { WatchType } from '../../../../components/form/elements/watch';
import Classifier from '../../../../enums/classifier';
import ClassifierGroup from '../../../../enums/classifier-group';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import { Unit } from '../../../../enums/unit';
import { useService } from '../../../../services/services';
import { round } from '../../../../services/utils';
import css from './needed.module.css';

interface Composition {
  n?: number;
  p2o5?: number;
  k2o?: number;
}

export default function Needed(
  updateCallback: (w: WatchType) => void
): FormComponent {
  return ({ ...rest }) => (
    <NeededComponent updateCallback={updateCallback} {...rest} />
  );
}

interface Props extends FormComponentProps {
  updateCallback: (w: WatchType) => void;
}

export function NeededComponent(props: Props) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.fertilization-plan.plan.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const { classifierService } = useService();

  const form = useFormContext();

  const getValues = (w: WatchType, keys: string[]) => {
    return keys.reduce((result, key) => {
      result[key] = w.getValues('needed.'.concat(key));
      return result;
    }, {} as { [key: string]: any });
  };

  const setValues = (w: WatchType, key: string, values: Composition) => {
    Object.keys(values).forEach((valueKey) =>
      w.setValue(
        `needed.${key}.${valueKey}`,
        values[valueKey as keyof Composition]
      )
    );
  };

  const calcNeedForForegrowthPostHarvestEffect = (w: WatchType) => {
    const {
      needForForegrowthPostHarvestEffect,
      needForForegrowthEffect,
      needForPlantNutrients,
    } = getValues(w, [
      'needForForegrowthPostHarvestEffect',
      'needForForegrowthEffect',
      'needForPlantNutrients',
    ]);
    let quantity = Number.parseFloat(
      w.getValues('foregrowth_postharvest_quantity')
    );
    quantity = Number.isNaN(quantity) || quantity < 0 ? 0 : quantity;
    if (!needForForegrowthPostHarvestEffect.set) {
      setValues(w, 'needForForegrowthPostHarvestEffect', {
        n: needForForegrowthEffect.n,
        p2o5: needForPlantNutrients.p2o5,
        k2o: needForPlantNutrients.k2o,
      });
    } else {
      setValues(w, 'needForForegrowthPostHarvestEffect', {
        n:
          needForForegrowthEffect.n +
          quantity * needForForegrowthPostHarvestEffect.nCorrection,
        p2o5:
          needForPlantNutrients.p2o5 +
          quantity * needForForegrowthPostHarvestEffect.p2o5Correction,
        k2o:
          needForPlantNutrients.k2o +
          quantity * needForForegrowthPostHarvestEffect.k2oCorrection,
      });
    }
  };

  const calcEffectOfForegrowthPostHarvest = (w: WatchType) => {
    const {
      needForForegrowthPostHarvestEffect,
      needForForegrowthEffect,
      needForPlantNutrients,
    } = getValues(w, [
      'needForForegrowthPostHarvestEffect',
      'needForForegrowthEffect',
      'needForPlantNutrients',
    ]);
    if (!needForForegrowthPostHarvestEffect.set) {
      setValues(w, 'effectOfForegrowthPostHarvest', {
        n: null,
        p2o5: null,
        k2o: null,
      });
    } else {
      setValues(w, 'effectOfForegrowthPostHarvest', {
        n: round(
          needForForegrowthPostHarvestEffect.n - needForForegrowthEffect.n
        ),
        p2o5: round(
          needForForegrowthPostHarvestEffect.p2o5 - needForPlantNutrients.p2o5
        ),
        k2o: round(
          needForForegrowthPostHarvestEffect.k2o - needForPlantNutrients.k2o
        ),
      });
    }
  };

  const calcNeedForGreenManureEffect = (w: WatchType) => {
    const { needForGreenManureEffect, needForForegrowthPostHarvestEffect } =
      getValues(w, [
        'needForGreenManureEffect',
        'needForForegrowthPostHarvestEffect',
      ]);
    const greenManure = w.getValues('green_manure');
    if (!needForGreenManureEffect.set) {
      setValues(w, 'needForGreenManureEffect', {
        n: needForForegrowthPostHarvestEffect.n,
      });
    } else {
      setValues(w, 'needForGreenManureEffect', {
        n:
          needForForegrowthPostHarvestEffect.n -
          (greenManure?.nCorrection || 0),
      });
    }
  };

  const calcEffectOfGreenManure = (w: WatchType) => {
    const { needForGreenManureEffect, needForForegrowthPostHarvestEffect } =
      getValues(w, [
        'needForGreenManureEffect',
        'needForForegrowthPostHarvestEffect',
      ]);
    if (!needForGreenManureEffect.set) {
      setValues(w, 'effectOfGreenManure', {
        n: null,
      });
    } else {
      setValues(w, 'effectOfGreenManure', {
        n: round(
          needForGreenManureEffect.n - needForForegrowthPostHarvestEffect.n
        ),
      });
    }
  };

  const calcNeedForForegrowthManureEffect = (w: WatchType) => {
    const {
      needForForegrowthManureEffect,
      needForGreenManureEffect,
      needForForegrowthPostHarvestEffect,
    } = getValues(w, [
      'needForForegrowthManureEffect',
      'needForGreenManureEffect',
      'needForForegrowthPostHarvestEffect',
    ]);
    if (!needForForegrowthManureEffect.set) {
      setValues(w, 'needForForegrowthManureEffect', {
        n: needForGreenManureEffect.n,
        p2o5: needForForegrowthPostHarvestEffect.p2o5,
        k2o: needForForegrowthPostHarvestEffect.k2o,
      });
    } else {
      setValues(w, 'needForForegrowthManureEffect', {
        n:
          needForGreenManureEffect.n -
          needForForegrowthManureEffect.nIntermediate,
        p2o5:
          needForForegrowthPostHarvestEffect.p2o5 -
          needForForegrowthManureEffect.p2o5Intermediate,
        k2o:
          needForForegrowthPostHarvestEffect.k2o -
          needForForegrowthManureEffect.k2oIntermediate,
      });
    }
  };

  const calcEffectOfForegrowthManure = (w: WatchType) => {
    const {
      needForForegrowthManureEffect,
      needForGreenManureEffect,
      needForForegrowthPostHarvestEffect,
    } = getValues(w, [
      'needForForegrowthManureEffect',
      'needForGreenManureEffect',
      'needForForegrowthPostHarvestEffect',
    ]);
    if (!needForForegrowthManureEffect.set) {
      setValues(w, 'effectOfForegrowthManure', {
        n: null,
        p2o5: null,
        k2o: null,
      });
    } else {
      setValues(w, 'effectOfForegrowthManure', {
        n: round(needForForegrowthManureEffect.n - needForGreenManureEffect.n),
        p2o5: round(
          needForForegrowthManureEffect.p2o5 -
            needForForegrowthPostHarvestEffect.p2o5
        ),
        k2o: round(
          needForForegrowthManureEffect.k2o -
            needForForegrowthPostHarvestEffect.k2o
        ),
      });
    }
  };

  const calcTotals = (w: WatchType) => {
    const { needForForegrowthManureEffect } = getValues(w, [
      'needForForegrowthManureEffect',
    ]);
    let neededOtherN = Number.parseInt(w.getValues('needed_other_n'));
    neededOtherN = Number.isNaN(neededOtherN) ? 0 : neededOtherN;
    let neededOtherP2O5 = Number.parseInt(w.getValues('needed_other_p2o5'));
    neededOtherP2O5 = Number.isNaN(neededOtherP2O5) ? 0 : neededOtherP2O5;
    let neededOtherK2O = Number.parseInt(w.getValues('needed_other_k2o'));
    neededOtherK2O = Number.isNaN(neededOtherK2O) ? 0 : neededOtherK2O;
    w.setValue(
      'needed_totals.n',
      round(needForForegrowthManureEffect.n + neededOtherN)
    );
    w.setValue(
      'needed_totals.p2o5',
      round(needForForegrowthManureEffect.p2o5 + neededOtherP2O5)
    );
    w.setValue(
      'needed_totals.k2o',
      round(needForForegrowthManureEffect.k2o + neededOtherK2O)
    );
  };

  const updateComposition = (w: WatchType) => {
    // as it is possible to change some values used in calculations,
    // we have to recalculate all related values here
    calcNeedForForegrowthPostHarvestEffect(w);
    calcEffectOfForegrowthPostHarvest(w);
    calcNeedForGreenManureEffect(w);
    calcEffectOfGreenManure(w);
    calcNeedForForegrowthManureEffect(w);
    calcEffectOfForegrowthManure(w);
    calcTotals(w);
    props.updateCallback(w);
  };

  const [Needed] = useState(() =>
    [
      'correctionForPlannedHarvest',
      'correctionForSowingPerennialGrasses',
      'correctionForGranulometricComposition',
      'correctionForOrganicMatterContent',
      'correctionForSoilPHKCl',
      'correctionForPlantNutrients',
      'effectOfForegrowth',
      'effectOfForegrowthPostHarvest',
      'effectOfGreenManure',
      'effectOfForegrowthManure',
    ].reduce(
      (needed, key) => {
        needed[key.charAt(0).toUpperCase() + key.slice(1)] = {
          N: Readonly(Text(`needed.${key}.n`, null, null, { inline: true })),
          P2O5: Readonly(
            Text(`needed.${key}.p2o5`, null, null, { inline: true })
          ),
          K2O: Readonly(
            Text(`needed.${key}.k2o`, null, null, { inline: true })
          ),
        };
        return needed;
      },
      {} as {
        [key: string]: {
          N: FormComponent;
          P2O5: FormComponent;
          K2O: FormComponent;
        };
      }
    )
  );

  const [ForegrowthPostharvestQuantity] = useState(() =>
    Watch(
      Amount(
        {
          amount: 'foregrowth_postharvest_quantity',
          unit: 'foregrowth_postharvest_quantity_unit',
        },
        {
          inline: true,
          onDisabled: 'both',
          disableUnit: true,
          options: [{ value: Unit.TON, label: Unit.TON }],
        }
      ),
      (w) => {
        const quantity = Number.parseFloat(w.value);
        if (Number.isNaN(quantity) || quantity < 0) {
          w.setValue('foregrowth_postharvest_quantity', '0');
        }
        updateComposition(w);
      }
    )
  );

  const [CorrectionForOtherN] = useState(() =>
    Watch(
      Integer('needed_other_n', null, null, true, { inline: true }),
      (w) => {
        const value = Number.parseFloat(w.value);
        if (Number.isNaN(value)) {
          w.setValue('needed_other_n', '0');
        }
        updateComposition(w);
      }
    )
  );

  const [CorrectionForOtherP2O5] = useState(() =>
    Watch(
      Integer('needed_other_p2o5', null, null, true, { inline: true }),
      (w) => {
        const value = Number.parseFloat(w.value);
        if (Number.isNaN(value)) {
          w.setValue('needed_other_p2o5', '0');
        }
        updateComposition(w);
      }
    )
  );

  const [CorrectionForOtherK2O] = useState(() =>
    Watch(
      Integer('needed_other_k2o', null, null, true, { inline: true }),
      (w) => {
        const value = Number.parseFloat(w.value);
        if (Number.isNaN(value)) {
          w.setValue('needed_other_k2o', '0');
        }
        updateComposition(w);
      }
    )
  );

  const [GreenManure] = useState(() =>
    Watch(
      Select('green_manure', {
        asyncOptions: (value, _, { page, parentName, getValues }) =>
          classifierService
            .valuesPaged(
              ClassifierGroup.CROP,
              Classifier.CORRECTION_BY_GREEN_MANURE_TYPE,
              { page, value }
            )
            .pipe(
              map((values) => ({
                hasMore: values.last_page > values.current_page,
                additional: { page: values.current_page + 1 },
                options: values.data.map((value) => ({
                  value: value.id,
                  label: value.value,
                  nCorrection:
                    Object.values(value.properties).find(
                      (p) =>
                        p.property.key === ClassifierPropertyKey.N_CORRECTION
                    )?.value || null,
                })),
              }))
            ),
        inline: true,
        clearable: false,
      }),
      (w) => updateComposition(w)
    )
  );

  return (
    <table className={css['needed']}>
      <tbody>
        <tr className={css['planned-harvest']}>
          <th>{tp('correction_for_planned_harvest')}</th>
          <td></td>
          <td>{<Needed.CorrectionForPlannedHarvest.N />}</td>
          <td>{<Needed.CorrectionForPlannedHarvest.P2O5 />}</td>
          <td>{<Needed.CorrectionForPlannedHarvest.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('correction_for_sowing_perennial_grasses')}</th>
          <td></td>
          <td>{<Needed.CorrectionForSowingPerennialGrasses.N />}</td>
          <td>{<Needed.CorrectionForSowingPerennialGrasses.P2O5 />}</td>
          <td>{<Needed.CorrectionForSowingPerennialGrasses.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('correction_for_granulometric_composition')}</th>
          <td></td>
          <td>{<Needed.CorrectionForGranulometricComposition.N />}</td>
          <td>{<Needed.CorrectionForGranulometricComposition.P2O5 />}</td>
          <td>{<Needed.CorrectionForGranulometricComposition.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('correction_for_organic_matter_content')}</th>
          <td></td>
          <td>{<Needed.CorrectionForOrganicMatterContent.N />}</td>
          <td>{<Needed.CorrectionForOrganicMatterContent.P2O5 />}</td>
          <td>{<Needed.CorrectionForOrganicMatterContent.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('correction_for_soil_phk_cl')}</th>
          <td></td>
          <td>{<Needed.CorrectionForSoilPHKCl.N />}</td>
          <td>{<Needed.CorrectionForSoilPHKCl.P2O5 />}</td>
          <td>{<Needed.CorrectionForSoilPHKCl.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('correction_for_plant_nutrients')}</th>
          <td></td>
          <td>{<Needed.CorrectionForPlantNutrients.N />}</td>
          <td>{<Needed.CorrectionForPlantNutrients.P2O5 />}</td>
          <td>{<Needed.CorrectionForPlantNutrients.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('effect_of_foregrowth')}</th>
          <td></td>
          <td>{<Needed.EffectOfForegrowth.N />}</td>
          <td>{<Needed.EffectOfForegrowth.P2O5 />}</td>
          <td>{<Needed.EffectOfForegrowth.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('effect_of_foregrowth_post_harvest')}</th>
          <td className={css['input']}>
            {<ForegrowthPostharvestQuantity readonly={props.readonly} />}
          </td>
          <td>{<Needed.EffectOfForegrowthPostHarvest.N />}</td>
          <td>{<Needed.EffectOfForegrowthPostHarvest.P2O5 />}</td>
          <td>{<Needed.EffectOfForegrowthPostHarvest.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('effect_of_green_manure')}</th>
          <td className={`${css['input']} ${css['green_manure']}`}>
            {props.readonly ? (
              form.getValues('green_manure')?.label
            ) : (
              <GreenManure />
            )}
          </td>
          <td>{<Needed.EffectOfGreenManure.N />}</td>
          <td>{<Needed.EffectOfGreenManure.P2O5 />}</td>
          <td>{<Needed.EffectOfGreenManure.K2O />}</td>
        </tr>
        <tr>
          <th>{tp('effect_of_foregrowth_manure')}</th>
          <td></td>
          <td>{<Needed.EffectOfForegrowthManure.N />}</td>
          <td>{<Needed.EffectOfForegrowthManure.P2O5 />}</td>
          <td>{<Needed.EffectOfForegrowthManure.K2O />}</td>
        </tr>
        <tr className={css['other-correction']}>
          <th>{tp('correction_for_other')}</th>
          <td></td>
          <td className={css['other-input']}>
            {<CorrectionForOtherN readonly={props.readonly} />}
          </td>
          <td className={css['other-input']}>
            {<CorrectionForOtherP2O5 readonly={props.readonly} />}
          </td>
          <td className={css['other-input']}>
            {<CorrectionForOtherK2O readonly={props.readonly} />}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
